/**
 * StorageManager - Centralized Storage Management Utility
 *
 * Provides a centralized interface for localStorage and sessionStorage operations
 * with error handling, type safety, and consistent behavior across the application.
 *
 * Features:
 * - Type-safe storage operations with generic support
 * - Automatic JSON serialization/deserialization
 * - Error handling with fallback behavior
 * - Support for both localStorage and sessionStorage
 * - Storage availability detection
 * - Batch operations for multiple keys
 *
 * @module utils/StorageManager
 */

import type { StorageKey } from '@/constants/storage';

/**
 * Storage type enumeration
 */
export type StorageType = 'localStorage' | 'sessionStorage';

/**
 * Storage operation result interface
 */
export interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Storage manager configuration
 */
export interface StorageConfig {
  /** Default storage type to use */
  defaultType: StorageType;
  /** Whether to log storage errors */
  logErrors: boolean;
  /** Prefix for all storage keys */
  keyPrefix?: string;
}

/**
 * Centralized storage management class
 */
export class StorageManager {
  private config: StorageConfig;
  private isAvailable: Record<StorageType, boolean>;

  constructor(config: Partial<StorageConfig> = {}) {
    this.config = {
      defaultType: 'localStorage',
      logErrors: true,
      ...config,
    };

    // Check storage availability
    this.isAvailable = {
      localStorage: this.checkStorageAvailability('localStorage'),
      sessionStorage: this.checkStorageAvailability('sessionStorage'),
    };
  }

  /**
   * Store a value in storage with automatic JSON serialization
   *
   * @param key - Storage key
   * @param value - Value to store
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result
   */
  setItem<T>(
    key: StorageKey | string,
    value: T,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<T> {
    try {
      if (!this.isAvailable[storageType]) {
        return {
          success: false,
          error: `${storageType} is not available`,
        };
      }

      const storage = this.getStorage(storageType);
      const serializedValue = this.safeStringify(value);
      const finalKey = this.formatKey(key);

      storage.setItem(finalKey, serializedValue);

      return {
        success: true,
        data: value,
      };
    } catch (error) {
      const errorMessage = `Failed to set ${key}: ${error instanceof Error ? error.message : 'Unknown error'}`;

      if (this.config.logErrors) {
        console.error('StorageManager.setItem:', errorMessage);
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Retrieve a value from storage with automatic JSON deserialization
   *
   * @param key - Storage key
   * @param defaultValue - Default value if key doesn't exist
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result
   */
  getItem<T>(
    key: StorageKey | string,
    defaultValue?: T,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<T> {
    try {
      if (!this.isAvailable[storageType]) {
        return {
          success: false,
          data: defaultValue,
          error: `${storageType} is not available`,
        };
      }

      const storage = this.getStorage(storageType);
      const finalKey = this.formatKey(key);
      const item = storage.getItem(finalKey);

      if (item === null) {
        return {
          success: true,
          data: defaultValue,
        };
      }

      const parsedValue = this.smartParse<T>(item, defaultValue);

      return {
        success: true,
        data: parsedValue,
      };
    } catch (error) {
      const errorMessage = `Failed to get ${key}: ${error instanceof Error ? error.message : 'Unknown error'}`;

      if (this.config.logErrors) {
        console.error('StorageManager.getItem:', errorMessage);
      }

      return {
        success: false,
        data: defaultValue,
        error: errorMessage,
      };
    }
  }

  /**
   * Remove an item from storage
   *
   * @param key - Storage key
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result
   */
  removeItem(
    key: StorageKey | string,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<void> {
    try {
      if (!this.isAvailable[storageType]) {
        return {
          success: false,
          error: `${storageType} is not available`,
        };
      }

      const storage = this.getStorage(storageType);
      const finalKey = this.formatKey(key);

      storage.removeItem(finalKey);

      return {
        success: true,
      };
    } catch (error) {
      const errorMessage = `Failed to remove ${key}: ${error instanceof Error ? error.message : 'Unknown error'}`;

      if (this.config.logErrors) {
        console.error('StorageManager.removeItem:', errorMessage);
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Check if a key exists in storage
   *
   * @param key - Storage key
   * @param storageType - Storage type (defaults to configured default)
   * @returns Whether the key exists
   */
  hasItem(
    key: StorageKey | string,
    storageType: StorageType = this.config.defaultType
  ): boolean {
    try {
      if (!this.isAvailable[storageType]) {
        return false;
      }

      const storage = this.getStorage(storageType);
      const finalKey = this.formatKey(key);

      return storage.getItem(finalKey) !== null;
    } catch (error) {
      if (this.config.logErrors) {
        console.error('StorageManager.hasItem:', error);
      }
      return false;
    }
  }

  /**
   * Clear all items from storage
   *
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result
   */
  clear(storageType: StorageType = this.config.defaultType): StorageResult<void> {
    try {
      if (!this.isAvailable[storageType]) {
        return {
          success: false,
          error: `${storageType} is not available`,
        };
      }

      const storage = this.getStorage(storageType);
      storage.clear();

      return {
        success: true,
      };
    } catch (error) {
      const errorMessage = `Failed to clear ${storageType}: ${error instanceof Error ? error.message : 'Unknown error'}`;

      if (this.config.logErrors) {
        console.error('StorageManager.clear:', errorMessage);
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Remove multiple items from storage
   *
   * @param keys - Array of storage keys
   * @param storageType - Storage type (defaults to configured default)
   * @returns Array of storage operation results
   */
  removeItems(
    keys: (StorageKey | string)[],
    storageType: StorageType = this.config.defaultType
  ): StorageResult<void>[] {
    return keys.map(key => this.removeItem(key, storageType));
  }

  /**
   * Get storage availability status
   *
   * @param storageType - Storage type to check
   * @returns Whether the storage type is available
   */
  isStorageAvailable(storageType: StorageType): boolean {
    return this.isAvailable[storageType];
  }

  /**
   * Get all keys from storage (with optional prefix filtering)
   *
   * @param storageType - Storage type (defaults to configured default)
   * @param filterPrefix - Optional prefix to filter keys
   * @returns Array of storage keys
   */
  getKeys(
    storageType: StorageType = this.config.defaultType,
    filterPrefix?: string
  ): string[] {
    try {
      if (!this.isAvailable[storageType]) {
        return [];
      }

      const storage = this.getStorage(storageType);
      const keys: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && (!filterPrefix || key.startsWith(filterPrefix))) {
          keys.push(key);
        }
      }

      return keys;
    } catch (error) {
      if (this.config.logErrors) {
        console.error('StorageManager.getKeys:', error);
      }
      return [];
    }
  }

  /**
   * Get the underlying storage object
   *
   * @private
   * @param storageType - Storage type
   * @returns Storage object
   */
  private getStorage(storageType: StorageType): Storage {
    return storageType === 'localStorage' ? localStorage : sessionStorage;
  }

  /**
   * Format storage key with optional prefix
   *
   * @private
   * @param key - Raw storage key
   * @returns Formatted storage key
   */
  private formatKey(key: StorageKey | string): string {
    return this.config.keyPrefix ? `${this.config.keyPrefix}_${key}` : key;
  }

  /**
   * Check if a storage type is available
   *
   * @private
   * @param storageType - Storage type to check
   * @returns Whether the storage is available
   */
  private checkStorageAvailability(storageType: StorageType): boolean {
    try {
      const storage = this.getStorage(storageType);
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Smart parsing with type-driven logic and multi-level fallback
   *
   * @private
   * @param value - Raw string value from storage
   * @param defaultValue - Default value for type inference
   * @returns Parsed value with appropriate type
   */
  private smartParse<T>(value: string, defaultValue?: T): T {
    // Handle empty strings and whitespace
    if (!value || value.trim() === '') {
      return defaultValue as T;
    }

    // Try type-driven parsing first if we have a default value
    if (defaultValue !== undefined) {
      try {
        const result = this.parseByTypeHint<T>(value, defaultValue);
        if (result !== undefined) {
          return result;
        }
      } catch {
        // Continue to next parsing method
      }
    }

    // Try JSON parsing
    try {
      return JSON.parse(value) as T;
    } catch {
      // Continue to auto-detection
    }

    // Try auto-detection parsing
    try {
      const result = this.autoDetectAndParse<T>(value);
      // Check if we got a valid result (including null and undefined)
      if (result !== undefined || value.trim() === 'undefined' || value.trim() === 'null') {
        return result as T;
      }
    } catch {
      // Continue to fallback
    }

    // Final fallback: return as string or default value
    return (value as unknown as T) || (defaultValue as T);
  }

  /**
   * Parse value based on the type hint from default value
   *
   * @private
   * @param value - Raw string value
   * @param defaultValue - Default value providing type hint
   * @returns Parsed value or undefined if parsing fails
   */
  private parseByTypeHint<T>(value: string, defaultValue: T): T | undefined {
    const defaultType = typeof defaultValue;

    switch (defaultType) {
      case 'string':
        return value as unknown as T;

      case 'number': {
        const numResult = this.parseNumber(value);
        return numResult !== null ? (numResult as unknown as T) : undefined;
      }

      case 'boolean': {
        const boolResult = this.parseBoolean(value);
        return boolResult !== null ? (boolResult as unknown as T) : undefined;
      }

      case 'object':
        if (defaultValue === null) {
          return value === 'null' ? (null as unknown as T) : undefined;
        }
        if (Array.isArray(defaultValue)) {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? (parsed as unknown as T) : undefined;
          } catch {
            return undefined;
          }
        }
        // For other objects, try JSON parsing
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' && parsed !== null ? (parsed as unknown as T) : undefined;
        } catch {
          return undefined;
        }

      default:
        return undefined;
    }
  }

  /**
   * Parse string to number with validation
   *
   * @private
   * @param value - String value to parse
   * @returns Parsed number or null if invalid
   */
  private parseNumber(value: string): number | null {
    // Handle common number formats
    const trimmed = value.trim();

    // Check for empty string
    if (trimmed === '') {
      return null;
    }

    // Try parsing as number
    const num = Number(trimmed);

    // Check if it's a valid number (not NaN and not Infinity)
    if (isNaN(num) || !isFinite(num)) {
      return null;
    }

    return num;
  }

  /**
   * Parse string to boolean with validation
   *
   * @private
   * @param value - String value to parse
   * @returns Parsed boolean or null if invalid
   */
  private parseBoolean(value: string): boolean | null {
    const trimmed = value.trim().toLowerCase();

    // True values
    if (trimmed === 'true' || trimmed === '1' || trimmed === 'yes' || trimmed === 'on') {
      return true;
    }

    // False values
    if (trimmed === 'false' || trimmed === '0' || trimmed === 'no' || trimmed === 'off') {
      return false;
    }

    return null;
  }

  /**
   * Auto-detect value type and parse accordingly
   *
   * @private
   * @param value - String value to parse
   * @returns Parsed value or undefined if detection fails
   */
  private autoDetectAndParse<T>(value: string): T | undefined {
    const trimmed = value.trim();

    // Check for null/undefined
    if (trimmed === 'null') {
      return null as unknown as T;
    }
    if (trimmed === 'undefined') {
      return undefined as unknown as T;
    }

    // Check for boolean
    const boolResult = this.parseBoolean(trimmed);
    if (boolResult !== null) {
      return boolResult as unknown as T;
    }

    // Check for number
    const numResult = this.parseNumber(trimmed);
    if (numResult !== null) {
      return numResult as unknown as T;
    }

    // Check for JSON objects/arrays
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed) as T;
      } catch {
        // Fall through to string
      }
    }

    // Default to string
    return trimmed as unknown as T;
  }

  /**
   * Get a string value from storage
   *
   * @param key - Storage key
   * @param defaultValue - Default string value
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result with string data
   */
  getString(
    key: StorageKey | string,
    defaultValue: string = '',
    storageType: StorageType = this.config.defaultType
  ): StorageResult<string> {
    return this.getItem<string>(key, defaultValue, storageType);
  }

  /**
   * Get a number value from storage
   *
   * @param key - Storage key
   * @param defaultValue - Default number value
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result with number data
   */
  getNumber(
    key: StorageKey | string,
    defaultValue: number = 0,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<number> {
    return this.getItem<number>(key, defaultValue, storageType);
  }

  /**
   * Get a boolean value from storage
   *
   * @param key - Storage key
   * @param defaultValue - Default boolean value
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result with boolean data
   */
  getBoolean(
    key: StorageKey | string,
    defaultValue: boolean = false,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<boolean> {
    return this.getItem<boolean>(key, defaultValue, storageType);
  }

  /**
   * Get an object value from storage
   *
   * @param key - Storage key
   * @param defaultValue - Default object value
   * @param storageType - Storage type (defaults to configured default)
   * @returns Storage operation result with object data
   */
  getObject<T extends object>(
    key: StorageKey | string,
    defaultValue?: T,
    storageType: StorageType = this.config.defaultType
  ): StorageResult<T> {
    return this.getItem<T>(key, defaultValue, storageType);
  }
  /**
   * Safe JSON stringify with circular reference detection
   *
   * @private
   * @param value - Value to stringify
   * @returns Stringified value
   * @throws Error if circular reference is detected
   */
  private safeStringify(value: unknown): string {
    const seen = new WeakSet();

    return JSON.stringify(value, (key, val) => {
      if (val !== null && typeof val === 'object') {
        if (seen.has(val)) {
          throw new Error('Converting circular structure to JSON');
        }
        seen.add(val);
      }
      return val;
    });
  }
}

/**
 * Default storage manager instance
 */
export const storageManager = new StorageManager({
  defaultType: 'localStorage',
  logErrors: process.env.NODE_ENV === 'development',
});

/**
 * Convenience functions using the default storage manager
 */
export const storage = {
  set: <T>(key: StorageKey | string, value: T, type?: StorageType) =>
    storageManager.setItem(key, value, type),

  get: <T>(key: StorageKey | string, defaultValue?: T, type?: StorageType) =>
    storageManager.getItem(key, defaultValue, type),

  getString: (key: StorageKey | string, defaultValue?: string, type?: StorageType) =>
    storageManager.getString(key, defaultValue, type),

  getNumber: (key: StorageKey | string, defaultValue?: number, type?: StorageType) =>
    storageManager.getNumber(key, defaultValue, type),

  getBoolean: (key: StorageKey | string, defaultValue?: boolean, type?: StorageType) =>
    storageManager.getBoolean(key, defaultValue, type),

  getObject: <T extends object>(key: StorageKey | string, defaultValue?: T, type?: StorageType) =>
    storageManager.getObject(key, defaultValue, type),

  remove: (key: StorageKey | string, type?: StorageType) =>
    storageManager.removeItem(key, type),

  has: (key: StorageKey | string, type?: StorageType) =>
    storageManager.hasItem(key, type),

  clear: (type?: StorageType) =>
    storageManager.clear(type),

  removeMultiple: (keys: (StorageKey | string)[], type?: StorageType) =>
    storageManager.removeItems(keys, type),
};

/**
 * Session storage specific utilities and convenience methods
 */
export const sessionStorageUtils = {
  /**
   * Set a value in sessionStorage
   */
  set: <T>(key: StorageKey | string, value: T) =>
    storageManager.setItem(key, value, 'sessionStorage'),

  /**
   * Get a value from sessionStorage
   */
  get: <T>(key: StorageKey | string, defaultValue?: T) =>
    storageManager.getItem(key, defaultValue, 'sessionStorage'),

  /**
   * Get a string value from sessionStorage
   */
  getString: (key: StorageKey | string, defaultValue?: string) =>
    storageManager.getString(key, defaultValue, 'sessionStorage'),

  /**
   * Get a number value from sessionStorage
   */
  getNumber: (key: StorageKey | string, defaultValue?: number) =>
    storageManager.getNumber(key, defaultValue, 'sessionStorage'),

  /**
   * Get a boolean value from sessionStorage
   */
  getBoolean: (key: StorageKey | string, defaultValue?: boolean) =>
    storageManager.getBoolean(key, defaultValue, 'sessionStorage'),

  /**
   * Get an object value from sessionStorage
   */
  getObject: <T extends object>(key: StorageKey | string, defaultValue?: T) =>
    storageManager.getObject(key, defaultValue, 'sessionStorage'),

  /**
   * Remove a value from sessionStorage
   */
  remove: (key: StorageKey | string) =>
    storageManager.removeItem(key, 'sessionStorage'),

  /**
   * Check if a key exists in sessionStorage
   */
  has: (key: StorageKey | string) =>
    storageManager.hasItem(key, 'sessionStorage'),

  /**
   * Clear all sessionStorage
   */
  clear: () =>
    storageManager.clear('sessionStorage'),

  /**
   * Remove multiple keys from sessionStorage
   */
  removeMultiple: (keys: (StorageKey | string)[]) =>
    storageManager.removeItems(keys, 'sessionStorage'),

  /**
   * Get all keys from sessionStorage
   */
  getKeys: (filterPrefix?: string) =>
    storageManager.getKeys('sessionStorage', filterPrefix),

  /**
   * Check if sessionStorage is available
   */
  isAvailable: () =>
    storageManager.isStorageAvailable('sessionStorage'),

  /**
   * Set a value with automatic expiration (session-specific utility)
   * The value will be automatically removed when retrieved after expiration
   */
  setWithExpiration: <T>(key: StorageKey | string, value: T, expirationMs: number): StorageResult<{ value: T; expiration: number }> => {
    const expirationTime = Date.now() + expirationMs;
    const wrappedValue = {
      value,
      expiration: expirationTime,
    };
    return storageManager.setItem(key, wrappedValue, 'sessionStorage');
  },

  /**
   * Get a value with automatic expiration check (session-specific utility)
   * Returns null if the value has expired and removes it from storage
   */
  getWithExpiration: <T>(key: StorageKey | string, defaultValue?: T): StorageResult<T> => {
    const result = storageManager.getItem<{ value: T; expiration: number }>(key, undefined, 'sessionStorage');

    if (!result.success || !result.data) {
      return {
        success: true,
        data: defaultValue,
      };
    }

    const { value, expiration } = result.data;

    // Check if expired
    if (Date.now() > expiration) {
      // Remove expired value
      storageManager.removeItem(key, 'sessionStorage');
      return {
        success: true,
        data: defaultValue,
      };
    }

    return {
      success: true,
      data: value,
    };
  },

  /**
   * Set a tab-specific value (uses tab ID for uniqueness)
   * Useful for storing data that should be unique per browser tab
   */
  setTabSpecific: <T>(key: StorageKey | string, value: T, tabId?: string): StorageResult<T> => {
    const actualTabId = tabId || `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const tabSpecificKey = `${key}_${actualTabId}`;
    return storageManager.setItem(tabSpecificKey, value, 'sessionStorage');
  },

  /**
   * Get a tab-specific value
   */
  getTabSpecific: <T>(key: StorageKey | string, tabId: string, defaultValue?: T): StorageResult<T> => {
    const tabSpecificKey = `${key}_${tabId}`;
    return storageManager.getItem(tabSpecificKey, defaultValue, 'sessionStorage');
  },

  /**
   * Remove a tab-specific value
   */
  removeTabSpecific: (key: StorageKey | string, tabId: string): StorageResult<void> => {
    const tabSpecificKey = `${key}_${tabId}`;
    return storageManager.removeItem(tabSpecificKey, 'sessionStorage');
  },

  /**
   * Get all tab-specific keys for a base key
   */
  getTabSpecificKeys: (baseKey: StorageKey | string): string[] => {
    const prefix = `${baseKey}_tab_`;
    return storageManager.getKeys('sessionStorage', prefix);
  },

  /**
   * Clean up expired tab-specific values
   */
  cleanupTabSpecific: (baseKey: StorageKey | string): void => {
    const tabKeys = sessionStorageUtils.getTabSpecificKeys(baseKey);
    tabKeys.forEach(key => {
      storageManager.removeItem(key, 'sessionStorage');
    });
  },
};

/**
 * Session storage manager instance configured specifically for sessionStorage
 */
export const sessionStorageManager = new StorageManager({
  defaultType: 'sessionStorage',
  logErrors: process.env.NODE_ENV === 'development',
});